import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { DateTime } from '@reynolds/datetime';
import css from './CalendarSelectAppt.module.scss';
import CalendarSelectApptTime from './CalendarSelectApptTime';
import CalendarSelectApptLabel from './CalendarSelectApptLabel';
import { TimeOfDay, Transportation, rmTimeRec, ShopHour, ApptOps, rmTransEligible, ascPageviewInput, VehicleRec, ascCtaInteractionInput, SpecData} from 'src/models';
import { convertTo24 } from 'src/helpers/TimeDisplayHelper/TimeDisplayHelper';
import CalendarSelectApptInfo from './CalendarSelectApptInfo';
import { FadeAndSlideTransition, IView } from '@framework-controls/core';
import ToastBar from 'src/components/ToastBar/ToastBar';
import { strings } from 'src/localization';
import moment from 'moment';
import 'moment/min/locales.min';
import { ComponentMapIds } from 'src/components/map';
import { ascCtaInteraction, ascPageview } from 'src/helpers/EventHelper/EventHelper';
import ScrollBar from 'src/components/ScrollBar/ScrollBar';
import classNames from 'classnames';
import { IAppointmentState } from 'src/store/states';
import { buildApptData, sendSdEvent, getProviderId } from 'src/helpers/ShiftDigitalHelper/ShiftDigitalHelper';

interface IProps {
    selectedDate: DateTime;
    setSelectedTime: (time: string, transportation: Transportation) => void;
    timeOfDay: TimeOfDay;
    transportation: Transportation[];
    rmTimes: rmTimeRec[];
    shopHours?: ShopHour[];
    theme: string;
    apptOps: ApptOps[];
    locale: string;
    rmTransEligible: rmTransEligible;
    mainPaneViewOrder: IView[];
    btnSelected: string;
    vehicles: VehicleRec[];
    selectedVehicle?: string;
    gmossVersion: string;
    appointment: IAppointmentState;
    specData?: SpecData;
    sessionId: string;
    lexusMCLaunched: string;
    toyotaMCLaunched: string;
    lexusTDMCLaunched: string;
    toyotaTDMCLaunched: string;
}

const CalendarSelectAppt = (props: IProps) => {
    const [selectedAppt, setSelectedAppt] = useState('');
    const [selectedType, setSelectedType] = useState('');
    const [infoOpen, setInfoOpen] = useState(false);
    const [transInfo, setTransInfo] = useState('');
    const [toastFlag, setToastFlag] = useState(false);

    const isSDAnalyticsEnabled =
        props.specData?.toyotaTrackingFlag === 'Y' ||
        props.specData?.lexusTrackingFlag === 'Y' ||
        props.specData?.mbzTrackingFlag === 'Y' ||
        props.specData?.mazdaCATrackingFlag === 'Y' ||
        props.specData?.vwTrackingFlag === 'Y' ||
        props.specData?.jaguarTrackingFlag === 'Y' ||
        props.specData?.landRoverTrackingFlag === 'Y';

    const timeSelection = useCallback((time: string, transportation: Transportation) => {
        setToastFlag(true);
        setTimeout(setToastFlag, 2000);
        props.setSelectedTime(time, transportation);
        // handle time click
    }, [props.setSelectedTime]);

    const timeHandler = useCallback((time: string, transportation: Transportation) => {
        setSelectedAppt(time);
        setSelectedType(transportation.type);
        timeSelection(time, transportation);
        // handle time click
    }, [timeSelection]);

    const infoHandler = useCallback((type: string, e:any) => {
        if (infoOpen && transInfo === type) {
            setInfoOpen(false);
        } else {
            setInfoOpen(true);
            setTransInfo(type);
        }
        const ascCtaInteractionInp : ascCtaInteractionInput = {
            pageType: 'service',
            elementText: '?',
            elementColor: '',
            elementOrder: '',
            elementType: 'scheduling_tool',
            elementSubType: 'information_link',
            eventAction: 'click',
            eventActionResult: infoOpen ? 'close' : 'open',
            linkUrl: '',
        };
        ascCtaInteraction(ascCtaInteractionInp, e, props.vehicles, props.selectedVehicle);
    }, [infoOpen, transInfo, props.vehicles, props.selectedVehicle]);

    const closeInfoHandler = useCallback((e: any) => {
        setInfoOpen(false);
        const ascCtaInteractionInp : ascCtaInteractionInput = {
            pageType: 'service',
            elementText: 'X',
            elementColor: '',
            elementOrder: '',
            elementType: 'scheduling_tool',
            elementSubType: 'nav_button',
            eventAction: 'click',
            eventActionResult: 'close',
            linkUrl: '',
        };
        ascCtaInteraction(ascCtaInteractionInp, e, props.vehicles, props.selectedVehicle);
    }, [props.selectedVehicle, props.vehicles]);

    // Filter out appt times

    const filteredTimes = useMemo(() => {
        const tempStart = parseInt(props.timeOfDay.startTime, 10);
        const tempEnd = parseInt(props.timeOfDay.endTime, 10);

        return (
            props.rmTimes.filter((x) => (convertTo24(x.displayTime) >= tempStart &&
                convertTo24(x.displayTime) <= tempEnd) || x.transFlag === 'N')
        );
    }, [props.rmTimes, props.timeOfDay]);

    const noAvailTimesClassName = classNames(
        css.noAvailTimes,
        {
            [css.noAvailTimesAlt]: props.theme === 'chevrolet' || props.theme === 'honda' ||
            props.theme === 'buick' || props.theme === 'bmw ' || props.theme === 'infiniti' ||
            props.theme === 'subaru' || props.theme ==='nissan' || props.theme === 'mercedesBenz' || props.theme === 'mazda' ||
            props.theme === 'acura' || props.theme === 'genesis' || props.theme === 'lexus',
        },
    );

    // Determine which transportation types are available based on the selected ops
    let waiterAvailable = true;
    let loanerAvailable = true;
    let rentalAvailable = true;
    let conciergeAvailable = true;
    let cadillacAvailable = true;
    let PPUDWLAvailable = true;
    props.apptOps.forEach((apptOp) => {
        if (apptOp.opWaiter === 'N') {
            waiterAvailable = false;
        }
        if (apptOp.opLoaner === 'N') {
            loanerAvailable = false;
        }
        if (apptOp.opRental === 'N') {
            rentalAvailable = false;
        }
        if (apptOp.opConcierge === 'N') {
            conciergeAvailable = false;
        }
        if (apptOp.opCadillac === 'N') {
            cadillacAvailable = false;
        }
        if (apptOp.opPPUDWL === 'N') {
            PPUDWLAvailable = false;
        }
    });

    const { labels, apptTimes } = useMemo(() => {
        const labels = [] as JSX.Element[];
        const apptTimes = [] as JSX.Element[];

        // Loop through each transportation type.  Build the labels and the times for that type
        props.transportation.forEach((trans, index) => {
            let ignoreTrans = false;
            if (trans.type === 'P' && !conciergeAvailable && props.gmossVersion !== '1.4.2') {
                ignoreTrans = true;
            }
            if (trans.type === 'C' && !cadillacAvailable) {
                ignoreTrans = true;
            }
            if (!ignoreTrans) {
                const label = (
                    <CalendarSelectApptLabel
                        key={index}
                        type={trans.type}
                        label={trans.title}
                        selected={selectedType === trans.type}
                        information={trans.description}
                        infoClick={infoHandler}
                        theme={props.theme}
                        selectedVehicle={props.selectedVehicle}
                    />
                );
                const rmTimes = filteredTimes.filter((x) => x.transFlag === trans.type);
                labels.push(label);

                /* Show all transportation types selected in specs. If there are no times available, show the notAvailTimes string.
                Trans Types P & U (with GM OSS version 1.4.2+) do not currently have any minimum or maximum job times. This means
                rmTransEligible and XAvailable will always be the same, so XAvailable must also be checked if false. */
                const keyBase = `${trans.type}-not-available`;
                const notAvailKey = `${keyBase}-${index}`;
                const noAvailKey = `${keyBase}-${index}`;
                if (rmTimes.length === 0) {
                    if (trans.type === 'N') {
                        const notAvail = (
                            <div key={notAvailKey} className={noAvailTimesClassName}>{strings.NotAvailDate}</div>
                        );
                        apptTimes.push(notAvail);
                    } else if ((trans.type === 'W' && (!props.rmTransEligible.transWaiterEligible || !waiterAvailable)) ||
                        (trans.type === 'L' && (!props.rmTransEligible.transLoanerEligible || !loanerAvailable)) ||
                        (trans.type === 'R' && (!props.rmTransEligible.transRentalEligible || !rentalAvailable)) ||
                        (trans.type === 'P' && (!props.rmTransEligible.transPPUDEligible || !conciergeAvailable) &&
                            props.gmossVersion === '1.4.2') ||
                        (trans.type === 'U' && (!props.rmTransEligible.transPPUDWLEligible || !PPUDWLAvailable))) {
                        const noAvail = (
                            <div key={noAvailKey} className={noAvailTimesClassName}>
                                {strings.NotEligibleService}<span>(s)</span>
                            </div>
                        );
                        apptTimes.push(noAvail);
                    } else {
                        const noAvail = (
                            <div key={noAvailKey} className={noAvailTimesClassName}>{strings.NoAvailTimes}</div>
                        );
                        apptTimes.push(noAvail);
                    }
                } else {
                    const apptTimeKey = `${trans.type}-${index}`;
                    const apptTime = (
                        <CalendarSelectApptTime
                            key={apptTimeKey}
                            type={trans.type}
                            timeHandler={timeHandler}
                            apptTimes={rmTimes}
                            selectedTime={selectedAppt}
                            selectedType={selectedType}
                            transportation={trans}
                            theme={props.theme}
                            vehicles={props.vehicles}
                            selectedVehicle={props.selectedVehicle}
                            specData={props.specData}
                            sessionId = {props.sessionId}
                            lexusMCLaunched = {props.lexusMCLaunched}
                            lexusTDMCLaunched = {props.lexusTDMCLaunched}
                            toyotaMCLaunched = {props.toyotaMCLaunched}
                            toyotaTDMCLaunched = {props.toyotaTDMCLaunched}
                            appointment = {props.appointment}
                        />
                    );
                    apptTimes.push(apptTime);
                }
            }
        });

        return { labels, apptTimes };
    }, [
        cadillacAvailable,
        conciergeAvailable,
        PPUDWLAvailable,
        filteredTimes,
        infoHandler,
        loanerAvailable,
        props?.theme,
        props?.transportation,
        rentalAvailable,
        selectedAppt,
        selectedType,
        timeHandler,
        waiterAvailable,
        props.rmTransEligible,
        props.vehicles,
        props.selectedVehicle,
        props.gmossVersion,
        noAvailTimesClassName,
        props.specData,
        props.sessionId,
        props.lexusMCLaunched,
        props.lexusTDMCLaunched,
        props.toyotaTDMCLaunched,
        props.toyotaMCLaunched,
        props.appointment,
    ]);

    const timesArea = useMemo(() => {
        const info = props.transportation.find((x) => x.type === transInfo)?.description;
        const transTitle = props.transportation.find((x) => x.type === transInfo)?.title;
        if (infoOpen) {
            return (
                <div className={css.infoArea}>
                    <CalendarSelectApptInfo
                        title={transTitle ? transTitle : ''}
                        information={info ? info : ''}
                        onClose={closeInfoHandler}
                        theme = {props.theme}
                    />
                </div>
            );
        } else {
            return (
                <div className={css.timesArea}>
                    <ScrollBar scrollHorizontal theme={props.theme}>
                        {apptTimes}
                    </ScrollBar>
                </div>
            );
        }
    }, [props.transportation, apptTimes, infoOpen, transInfo, closeInfoHandler, props.theme]);

    const processingDate = props.selectedDate.toDate();
    let processingUTCDate = moment.utc(processingDate);
    processingUTCDate = processingUTCDate.locale(props.locale);
    const dateDOWLit = processingUTCDate.format('dddd').toUpperCase();
    const dateMonthLit = processingUTCDate.format('MMMM').toUpperCase();

    let message = strings.TimerDay[0].toUpperCase() + strings.TimerDay.slice(1) + ': ';

    message += dateDOWLit + ', ';
    message += dateMonthLit + ' ' + props.selectedDate.get('date') + ', ' + props.selectedDate.toDate().getFullYear() + '\n';
    message += strings.Time + selectedAppt;

    // The message is a little different if Night Drop
    if (selectedType === 'N') {
        message = strings.TimerDay[0].toUpperCase() + strings.TimerDay.slice(1) + ': ';
        message += dateDOWLit + ', ';
        message += dateMonthLit + ' ' + props.selectedDate.get('date') + ', ' +
            props.selectedDate.toDate().getFullYear() + '\n';
        message += strings.Time + strings.DropOff + selectedAppt;
    }

    let numTimes = 0;
    for (let k = 0; k < apptTimes.length; k++) {
        if (apptTimes[k].props.apptTimes) {
            numTimes = numTimes + apptTimes[k].props.apptTimes.length;
        }
    }

    // need to stop this from triggering when coming back from the summary screen.
    useEffect(() => {
        if (props.mainPaneViewOrder[props.mainPaneViewOrder.length - 1].viewId === ComponentMapIds.CalendarSelect &&
            props.btnSelected === 'apptSelection') {
            const ascPageViewInp: ascPageviewInput = {
                pageType: 'service',
                errorCode: '',
                itemResults: numTimes.toString(),
            };
            ascPageview(ascPageViewInp, props.vehicles, props.selectedVehicle);

            if (props.appointment && isSDAnalyticsEnabled) {
                sendSdEvent('scheduleServiceApptShown', buildApptData(props.sessionId,
                    getProviderId(props.specData, props.toyotaMCLaunched, props.toyotaTDMCLaunched,
                        props.lexusMCLaunched, props.lexusTDMCLaunched), props.appointment));
            }

            if (isSDAnalyticsEnabled) {
                sendSdEvent('scheduleServiceTransportShown', {
                    scheduleServiceSessionId: props.sessionId,
                    scheduleServiceProvider: getProviderId(props.specData, props.toyotaMCLaunched,
                        props.toyotaTDMCLaunched, props.lexusMCLaunched, props.lexusTDMCLaunched),
                    scheduleServiceTransportType: '',
                });
            }
        }
    }, [props.mainPaneViewOrder, numTimes, props.btnSelected, props.vehicles, props.selectedVehicle,
        props.appointment, props.specData, props.toyotaMCLaunched, props.toyotaTDMCLaunched,
        props.lexusMCLaunched, props.lexusTDMCLaunched, props.sessionId, isSDAnalyticsEnabled,
    ]);

    const labelAreaClass = classNames(
        css.labelArea,
        {
            [css.labelAreaIN]: props.theme === 'infiniti',
        },
    );

    return (
        <div className={css.calendarAppt}>
            <div className={labelAreaClass}>
                {labels}
            </div>
            {timesArea}
            {toastFlag ?
                <FadeAndSlideTransition
                    in={toastFlag}
                    timeout={{
                        enter: 0,
                        exit: 100,
                    }}
                    isTop
                    zIndex={0}>
                    <ToastBar
                        type='Appt'
                        message={message}
                        theme={props.theme} />
                </FadeAndSlideTransition> :
                null
            }
        </div>
    );
};

export default CalendarSelectAppt;